import { useTranslation } from 'react-i18next';
import EPageHeader from '@/components/everridge/EPageHeader';
import ESearchInput from '@/components/everridge/ESearchInput';
import TransactionsFilter from '@/features/transactions/components/TransactionsFilter';
import TransactionsPageColumns from '@/features/transactions/components/TransactionsPageColumns';
import { useState } from 'react';
import TransactionDetails from '@/features/transactions/components/TransactionDetails';
import { useTransactionsListApi } from '@/features/transactions/hooks/useTransactionsListApi';
import { FilterHelper } from '@/utils/filterHelper';
import ETableWithApiPagination from '@/components/everridge/table/ETableWithApiPagination';
import { useTransactionsFilter } from '@/features/transactions/hooks/useTransactionsFilter';
import { Button } from '@/components/ui/button';
import FilterIcon from '@/components/icons/FilterIcon';
import useMediaQuery from '@/hooks/useMediaQuery';
import { AppConfig } from '@/config/app.config';
import { cn } from '@/lib/utils';
import TransactionsTableMobile from '@/features/transactions/components/TransactionsTableMobile';
import { useSearchParams } from 'react-router-dom';
import Download_04_Icon from '@/components/icons/Download_04_Icon';
import TransactionsInvoicePopup from './components/TransactionsInvoicePopup';

const TransactionsPage = () => {
    const [searchText, setSearchText] = useState('');
    const [transactionId, setTransactionId] = useState('');
    const [showTransactionDetails, setShowTransactionDetails] = useState(false);
    const [showTransactionFilter, setShowTransactionFilter] = useState(false);
    const [showInvoicePopup, setShowInvoicePopup] = useState(false);
    const [page, setPage] = useState(0);

    const { t } = useTranslation();
    const columns = TransactionsPageColumns();
    const isMobile = useMediaQuery(AppConfig.mediaQueries.mobile);

    const [searchParams] = useSearchParams();
    const ownerId = searchParams.get('ownerId') || '';

    const { transactionsListData, isLoadingTransactionsListData } = useTransactionsListApi({
        searchText,
        ownerId,
    });

    const { filter, setFilter } = useTransactionsFilter();

    const handleShowTransaction = (id: string) => {
        setTransactionId(id);
        setShowTransactionDetails(true);
    };

    const handleCloseTransactionDetails = () => {
        setTransactionId('');
        setShowTransactionDetails(false);
    };

    const handlePageChange = (newPage: number) => {
        setPage(newPage);
        setFilter({
            skip: filter.take * newPage,
        });
    };

    const filtersQuantity = FilterHelper.countFilterValues(filter, [
        'orderBy',
        'orderDirection',
        'skip',
        'take',
    ]);

    return (
        <section className={'flex flex-col gap-6'}>
            <EPageHeader
                title={t('main.transactions.title')}
                search={
                    <ESearchInput
                        value={searchText}
                        setValue={setSearchText}
                        placeholder={t('main.transactions.form.search.placeholder')}
                        containerClassName={cn(!isMobile && 'w-60')}
                    />
                }
                addBackButton={true}
            >
                <div className="relative">
                    {!!filtersQuantity && (
                        <div className="absolute -top-1 right-0 size-4 rounded-full bg-error-1000 flex items-center justify-center text-2xs text-popover-10 animate-pulse ">
                            {filtersQuantity}
                        </div>
                    )}
                    <Button variant="outline" onClick={() => setShowTransactionFilter(true)}>
                        <FilterIcon />
                        {t('elements.buttons.filter')}
                    </Button>
                </div>
                <Button onClick={() => setShowInvoicePopup(true)}>
                    <Download_04_Icon />
                    {t('elements.buttons.invoice')}
                </Button>
            </EPageHeader>
            {!isMobile ? (
                <ETableWithApiPagination
                    columns={columns}
                    data={transactionsListData}
                    page={page}
                    setPage={handlePageChange}
                    showDetails={handleShowTransaction}
                />
            ) : (
                <TransactionsTableMobile
                    data={transactionsListData}
                    isLoading={isLoadingTransactionsListData}
                    page={page}
                    setPage={handlePageChange}
                    showDetails={handleShowTransaction}
                />
            )}
            {showTransactionDetails && (
                <TransactionDetails id={transactionId} onClose={handleCloseTransactionDetails} />
            )}
            {showTransactionFilter && (
                <TransactionsFilter onClose={() => setShowTransactionFilter(false)} />
            )}
            {showInvoicePopup && (
                <TransactionsInvoicePopup onClose={() => setShowInvoicePopup(false)} />
            )}
        </section>
    );
};

export default TransactionsPage;
